import React from 'react';
import { adresaSablony } from '../utils/adresaUdalosti';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Divider,
} from '@mui/material';
import { Campaign as CampaignIcon } from '@mui/icons-material';
import { useVyhlasitZeSablonyMutation } from '../api/JsdhNotifikaceService/service/AdminMutations';
import type { SablonaUdalosti } from '../api/JsdhNotifikaceService/generated';

interface PotvrzeniVyhlaseniDialogProps {
    open: boolean;
    onClose: () => void;
    sablona: SablonaUdalosti | null;
    onSuccess?: () => void;
}

const PotvrzeniVyhlaseniDialog: React.FC<PotvrzeniVyhlaseniDialogProps> = ({
    open,
    onClose,
    sablona,
    onSuccess,
}) => {
    const vyhlasitMutation = useVyhlasitZeSablonyMutation();

    const handleConfirm = () => {
        if (!sablona) return;
        vyhlasitMutation.mutate(
            { id: sablona.id },
            {
                onSuccess: () => {
                    onClose();
                    onSuccess?.();
                },
            }
        );
    };

    if (!sablona) return null;

    const adresa = adresaSablony(sablona);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: 'error.main' }}>
                Opravdu vyhlásit poplach?
            </DialogTitle>

            <DialogContent>
                <Box display="flex" flexDirection="column" gap={1.5} pt={1}>
                    {vyhlasitMutation.isError && (
                        <Alert severity="error">
                            {vyhlasitMutation.error?.message || 'Nepodařilo se vyhlásit poplach'}
                        </Alert>
                    )}

                    <Typography variant="subtitle1" fontWeight="bold">
                        {sablona.nazev}
                    </Typography>

                    {sablona.popis && (
                        <Typography variant="body2" color="text.secondary">
                            {sablona.popis}
                        </Typography>
                    )}

                    <Divider />

                    {adresa && (
                        <Box display="flex" gap={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                                Adresa:
                            </Typography>
                            <Typography variant="body2">
                                {adresa}
                            </Typography>
                        </Box>
                    )}

                    {sablona.objekt && (
                        <Box display="flex" gap={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                                Objekt:
                            </Typography>
                            <Typography variant="body2">{sablona.objekt}</Typography>
                        </Box>
                    )}

                    {sablona.technikaJednotky && sablona.technikaJednotky.length > 0 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Technika:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {sablona.technikaJednotky.map((t, i) => (
                                    <Chip key={i} label={t} size="small" />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Divider />

                    <Box display="flex" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            Notifikace: {sablona.odeslatNotifikace !== false ? 'Ano' : 'Ne'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            TTS: {sablona.generovatTts !== false ? 'Ano' : 'Ne'}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={vyhlasitMutation.isPending}>
                    Zrušit
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={vyhlasitMutation.isPending}
                    startIcon={vyhlasitMutation.isPending ? <CircularProgress size={20} /> : <CampaignIcon />}
                >
                    {vyhlasitMutation.isPending ? 'Vyhlašuji...' : 'VYHLÁSIT POPLACH'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PotvrzeniVyhlaseniDialog;
